import {
  InputDescription,
  InputHint,
} from "@design-system/components/Form/Utils";
import { SelectContext, useSelectManager } from "@design-system/hooks";
import type { SelectInputProps } from "@design-system/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@design-system/ui/dropdown-menu";
import {
  SelectInputEmpty,
  SelectInputError,
  SelectInputGroups,
  SelectInputHeading,
  SelectInputLabel,
  SelectInputLoading,
  SelectInputSearch,
} from "./SelectComponents";

/**
 * SelectInput component for single option selection
 * 
 * @param props - SelectInputProps including options, autoSelectSingleOption, etc.
 * @param props.autoSelectSingleOption - When true, automatically selects the option if there's only one available and no value is currently selected
 * @returns JSX.Element
 */
export function SelectInput(props: SelectInputProps) {
  const selectManager = useSelectManager(props);

  const { id, opened, setOpened } = selectManager;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <SelectContext.Provider value={selectManager}>
        <SelectInputLabel />
        {props.description && (
          <InputDescription>{props.description}</InputDescription>
        )}
        <DropdownMenu open={opened} onOpenChange={setOpened}>
          {/* asChild: the heading is itself a <button>; nesting it inside the
              trigger's <button> is invalid HTML and broke hydration. */}
          <DropdownMenuTrigger
            asChild
            disabled={props.disabled}
            title={props.placeholder}
            id={id}
          >
            <SelectInputHeading />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="z-[9999] w-[var(--radix-dropdown-menu-trigger-width)] p-0 bg-popover dark:bg-gray-800 border-input dark:border-gray-600 shadow-lg dark:shadow-xl"
          >
            <SelectInputSearch />
            <SelectInputLoading />
            <SelectInputEmpty />
            <SelectInputGroups />
          </DropdownMenuContent>
        </DropdownMenu>
        {props.hint && !selectManager.error && (
          <InputHint>{props.hint}</InputHint>
        )}
        <SelectInputError />
      </SelectContext.Provider>
    </div>
  );
}
